/**
 * Content-addressed binary ASSET STORE — Bombproof storage foundation, Layer 2.
 *
 * Heavy image payloads (base64 `data:` URLs) currently live INLINE in
 * `ImageObject.properties.src`, which serializes into the project document and
 * overflows the small localStorage tier (board 12-imgstore / WD-11). This store
 * holds those payloads BY REFERENCE, keyed by a content hash, so a document ever
 * only carries a lightweight id and never the bytes.
 *
 * Content-addressing (sha256 of the payload) gives, for free:
 *  - **idempotent migration** — re-migrating the same image yields the same id,
 *    never a duplicate/orphan (the property R3 required at Stage 5);
 *  - **dedup** — the same image imported/pasted twice is stored once;
 *  - a stable basis for a future refcount-GC (deferred this chunk).
 *
 * The backend is INJECTED (see {@link AssetBackend}) so the store's logic is
 * unit-testable without a live IndexedDB; production wires the IndexedDB backend
 * ({@link IndexedDbAssetBackend}). Design of record:
 * architecture/Groups/Bombproof/chunk-8.4/close-campaign/imgstore-fix-s1/approval-gate.md §6-§7.
 */

/**
 * Persistence backend for the asset store. Implementations map a content-id to a
 * stored payload string. Kept intentionally tiny so it can be backed by
 * IndexedDB in production and an in-memory map in tests.
 */
export interface AssetBackend {
  /** Return the stored payload for `id`, or null if absent. */
  get(id: string): Promise<string | null>;
  /** Store `value` under `id` (overwrite-safe; callers dedup via {@link AssetStore.put}). */
  put(id: string, value: string): Promise<void>;
  /** True iff a payload is stored under `id`. */
  has(id: string): Promise<boolean>;
  /**
   * Enumerate every stored content id. The GC mark-sweep's candidate set
   * ({@link assetGarbageCollect}). MUST surface a backend failure (throw), never
   * a fail-silent empty list — an unenumerable store is ambiguous, and GC refuses
   * on ambiguity rather than treating "couldn't list" as "nothing stored".
   */
  keys(): Promise<string[]>;
  /** Remove the payload stored under `id` (idempotent; absent id is a no-op). */
  delete(id: string): Promise<void>;
}

/** Maps a payload string to a stable content id (hex). */
export type Hasher = (value: string) => Promise<string>;

/**
 * sha256 (hex) of the UTF-8 bytes of `value`, via WebCrypto — available in every
 * browser that runs Stagecraft (WebGL2-class) and in Node ≥15 (`globalThis.crypto`).
 */
export const sha256Hex: Hasher = async (value: string): Promise<string> => {
  const bytes = new TextEncoder().encode(value);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

/**
 * Content-addressed asset store. Stateless over its injected backend + hasher.
 */
export class AssetStore {
  /**
   * Content ids this SESSION has stored (the GC race guard — imgstore-gc-s1).
   * A blob an in-flight save has `put` may not yet have its referencing document
   * written to disk; the mark-sweep would then see it as an orphan. Every `put`
   * records its id here BEFORE persisting the payload (claim-first ordering), and
   * {@link assetGarbageCollect} NEVER deletes a claimed id — so a blob the running
   * session has stored is un-sweepable regardless of scan timing. In-memory +
   * session-scoped by design: cleared on reload, so a next-session boot sweep
   * still reclaims a genuinely-orphaned blob.
   */
  private readonly claimed = new Set<string>();

  constructor(
    private readonly backend: AssetBackend,
    private readonly hash: Hasher = sha256Hex,
  ) {}

  /**
   * Store `value` and return its content-addressed id. IDEMPOTENT: identical
   * content yields the same id and is written at most once (the `has` guard is
   * the dedup + re-migration-safety point). The id is CLAIMED before the payload
   * is persisted (see {@link claimed}) so a concurrent GC cannot delete it.
   */
  async put(value: string): Promise<string> {
    const id = await this.hash(value);
    // Claim FIRST — before any backend write. Any id present in the backend was
    // therefore claimed no later than it was persisted, so a GC that sees a blob
    // via keys() will also see its claim (GC reads keys() before claimedIds()).
    this.claimed.add(id);
    if (!(await this.backend.has(id))) {
      await this.backend.put(id, value);
    }
    return id;
  }

  /** Resolve a content id back to its payload, or null if the asset is missing. */
  async get(id: string): Promise<string | null> {
    if (!id) return null;
    return this.backend.get(id);
  }

  /** True iff `id` resolves to a stored payload. */
  async has(id: string): Promise<boolean> {
    if (!id) return false;
    return this.backend.has(id);
  }

  /** Enumerate every stored content id (GC candidate set). Surfaces backend errors. */
  async keys(): Promise<string[]> {
    return this.backend.keys();
  }

  /** Remove the payload under `id` (GC sweep). No-op for a falsy id. */
  async delete(id: string): Promise<void> {
    if (!id) return;
    return this.backend.delete(id);
  }

  /** Ids the running session has stored — never GC-eligible (the race guard). */
  claimedIds(): ReadonlySet<string> {
    return this.claimed;
  }

  /** Test-only: drop session claims so a cell can drive a fresh boot-sweep race. */
  clearClaimsForTests(): void {
    this.claimed.clear();
  }
}

/**
 * In-memory {@link AssetBackend} — the test/fallback backend. Also the safe
 * degradation target if IndexedDB is somehow unavailable AFTER the startup
 * capability gate (session-only; never a localStorage document mirror).
 */
export class InMemoryAssetBackend implements AssetBackend {
  private readonly map = new Map<string, string>();

  async get(id: string): Promise<string | null> {
    return this.map.has(id) ? (this.map.get(id) as string) : null;
  }

  async put(id: string, value: string): Promise<void> {
    this.map.set(id, value);
  }

  async has(id: string): Promise<boolean> {
    return this.map.has(id);
  }

  async keys(): Promise<string[]> {
    return Array.from(this.map.keys());
  }

  async delete(id: string): Promise<void> {
    this.map.delete(id);
  }

  /** Test/introspection helper — number of distinct assets stored. */
  get size(): number {
    return this.map.size;
  }
}
